/*
 * What the copilot is looking at — the browser's half of §13's "one ask-route per module".
 *
 * ── Why a registry rather than the URL ─────────────────────────────────────────────────────────
 *
 * The obvious implementation reads the address bar: `/jobs/$jobId` means ask the job route. It
 * falls over on the first screen that is about something the path does not name — the pipeline
 * board is one URL over many applications, the interview tracker is one URL over many candidacies,
 * and Home is about a desk that has no id at all. The shell would need a table of route patterns to
 * subject kinds, which is screen knowledge living in the frame, and every new screen would silently
 * inherit the wrong context until somebody remembered to add a row.
 *
 * So a screen **declares** what it is about, exactly as it already declares its keyboard shortcuts,
 * and the flyout asks whichever module owns that kind. A screen that declares nothing gets the desk,
 * which is the honest default: standing on a list of jobs, the question a person asks is about their
 * own work, not about a requisition they have not opened.
 *
 * ── The two-context split is copied from `keyboard/shortcuts.tsx`, and for the same reason ─────
 *
 * That file records what happened when `register` and the registered value shared one context: the
 * value changed on every registration, so every consumer's effect re-ran, so every screen
 * unregistered and re-registered — three registrations and two removals for one mount. The setter
 * lives in a context whose value never changes; the target lives in its own, read by the flyout,
 * which is the only thing that has to re-render when it moves.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * What a screen is about.
 *
 * `desk` carries no id, which is the shape of the thing rather than an omission: the desk ask route
 * scopes itself to the caller's own membership and takes nothing from the browser. A union rather
 * than an optional id, so a screen cannot declare a job without saying which one.
 */
export type CopilotTarget =
  | { readonly kind: 'job'; readonly id: string; readonly label: string }
  | { readonly kind: 'candidate'; readonly id: string; readonly label: string }
  | { readonly kind: 'application'; readonly id: string; readonly label: string }
  | { readonly kind: 'desk'; readonly label?: undefined };

export const DESK: CopilotTarget = { kind: 'desk' };

type Announce = (target: CopilotTarget) => () => void;

const AnnounceContext = createContext<Announce | null>(null);
const TargetContext = createContext<CopilotTarget>(DESK);

/**
 * §13 E5 — "discuss this", from a why-popover or an artifact's toolbar.
 *
 * A *channel* rather than a callback passed down: the thing that wants to start a conversation is
 * an inline control four levels inside a screen, and the thing that shows it is the shell. Threading
 * a prop between them would put the flyout in the signature of every component between the two, and
 * every one of them would have to keep passing it whether or not it cared.
 *
 * `pending` is a question waiting to be asked, and it carries a nonce so that discussing the *same*
 * artifact twice is two events rather than one state that did not change.
 */
interface Session {
  readonly showing: boolean;
  readonly open: () => void;
  readonly close: () => void;
  readonly discuss: (question: string) => void;
  readonly pending: { readonly question: string; readonly nonce: number } | null;
  readonly clearPending: () => void;
}

const SessionContext = createContext<Session | null>(null);

export function CopilotProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<CopilotTarget>(DESK);
  const [showing, setShowing] = useState(false);
  const [pending, setPending] = useState<{ question: string; nonce: number } | null>(null);
  const nonce = useRef(0);
  /*
   * A stack, not a value.
   *
   * Screens nest: a job record can open a drawer about one application inside it, and the drawer
   * unmounting must put the job back rather than dropping to the desk. Keeping the whole stack makes
   * "what is on top" a fact rather than a guess, and makes the unmount case correct without either
   * component knowing about the other.
   */
  const stack = useRef<CopilotTarget[]>([]);

  const settle = useCallback(() => {
    setTarget(stack.current[stack.current.length - 1] ?? DESK);
  }, []);

  const announce = useCallback<Announce>(
    (next) => {
      stack.current = [...stack.current, next];
      settle();
      return () => {
        stack.current = stack.current.filter((one) => one !== next);
        settle();
      };
    },
    [settle],
  );

  const session = useMemo<Session>(
    () => ({
      showing,
      open: () => setShowing(true),
      close: () => setShowing(false),
      discuss: (question: string) => {
        nonce.current += 1;
        setPending({ question, nonce: nonce.current });
        setShowing(true);
      },
      pending,
      clearPending: () => setPending(null),
    }),
    [showing, pending],
  );

  return (
    <AnnounceContext.Provider value={announce}>
      <SessionContext.Provider value={session}>
        <TargetContext.Provider value={target}>{children}</TargetContext.Provider>
      </SessionContext.Provider>
    </AnnounceContext.Provider>
  );
}

/**
 * The flyout's open state and the "discuss this" channel.
 *
 * Returns a no-op session outside the provider rather than throwing: the sign-in screen renders
 * outside it, and a component that crashed there would trade a missing button for a blank page.
 */
export function useCopilotSession(): Session {
  return (
    useContext(SessionContext) ?? {
      showing: false,
      open: () => {},
      close: () => {},
      discuss: () => {},
      pending: null,
      clearPending: () => {},
    }
  );
}

/**
 * Declares what this screen is about, for as long as it is mounted.
 *
 * The identity of the object matters — it is what the cleanup removes from the stack — so it is
 * rebuilt only when the values change. A screen passing an inline literal would otherwise push a
 * new entry on every render and leave the stack growing until it unmounted.
 */
export function useCopilotTarget(target: CopilotTarget | null): void {
  const announce = useContext(AnnounceContext);

  /*
   * `null` is a first-class argument, not a convenience.
   *
   * Every record screen computes its target from a query, and every one of them returns early while
   * that query is pending — so a hook that demanded a whole target could only be called *after* the
   * early return, which is a rules-of-hooks violation. Passing null while the record loads is the
   * only shape that lets the call sit at the top of the component where hooks belong, and it says
   * something true besides: until the record arrives, this screen is not about anything yet.
   */
  const key = target === null ? '' : target.kind === 'desk' ? 'desk' : `${target.kind}:${target.id}:${target.label}`;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the target's value identity.
  const stable = useMemo(() => target, [key]);

  useEffect(() => {
    if (!announce || stable === null) return;
    return announce(stable);
  }, [announce, stable]);
}

/** What the flyout should ask about. `desk` when no screen has said otherwise. */
export function useCopilotContext(): CopilotTarget {
  return useContext(TargetContext);
}
